import { Suspense, lazy, useEffect, useState } from "react";
import "./App.css";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { dteApi } from "./lib/api";
import { Menu, X } from "lucide-react";

import Sidebar from "./components/Sidebar";
import AssistantWidget from "./components/AssistantWidget";
import MarketingConsentBanner from "./components/MarketingConsentBanner";
import QuotaBanner from "./components/QuotaBanner";
import UpgradeModal from "./components/UpgradeModal";
import { IntegrationsHealthProvider } from "./context/IntegrationsHealthContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Configuracion = lazy(() => import("./pages/Configuracion"));
const GuiaConfiguracionFiscal = lazy(() => import("./pages/GuiaConfiguracionFiscal"));
const Factura = lazy(() => import("./pages/Factura"));
const Historial = lazy(() => import("./pages/Historial"));
const Invalidacion = lazy(() => import("./pages/Invalidacion"));
const Contingencia = lazy(() => import("./pages/Contingencia"));
const EventoRetorno = lazy(() => import("./pages/EventoRetorno"));
const Contabilidad = lazy(() => import("./pages/Contabilidad"));
const Recibidos = lazy(() => import("./pages/Recibidos"));
const Declaraciones = lazy(() => import("./pages/Declaraciones"));
const Equipo = lazy(() => import("./pages/Equipo"));
const DteForm = lazy(() => import("./components/DteForm"));
const AutoPruebas = lazy(() => import("./pages/AutoPruebas"));
import RequirePruebasEnvironment from "./components/RequirePruebasEnvironment";
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
import ProtectedRoute from "./components/ProtectedRoute";
const Clientes = lazy(() => import("./pages/Clientes"));
const Inventario = lazy(() => import("./pages/Inventario"));
const Alertas = lazy(() => import("./pages/Alertas"));
const Importaciones = lazy(() => import("./pages/Importaciones"));
const Reportes = lazy(() => import("./pages/Reportes"));
const Suscripcion = lazy(() => import("./pages/Suscripcion"));
const Admin = lazy(() => import("./pages/Admin"));
const PaymentResult = lazy(() => import("./pages/PaymentResult"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Miembros = lazy(() => import("./pages/Miembros"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Landing = lazy(() => import("./pages/Landing"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AmbienteProvider, useAmbiente } from "./context/AmbienteContext";
import { useSubscription } from "./context/SubscriptionContext";
import { IS_LOCAL_EDITION } from "./lib/edition";
import { initializeMarketingAnalytics, subscribeToMarketingConsent } from "./lib/marketingAnalytics";

function MarketingAnalyticsBootstrap() {
  useEffect(() => {
    const initialize = () => initializeMarketingAnalytics();
    initialize();
    return subscribeToMarketingConsent(initialize);
  }, []);

  return null;
}

function PublicLanding() {
  if (IS_LOCAL_EDITION) return <Navigate to="/dashboard" replace />;
  return (
    <>
      <Suspense fallback={null}>
        <Landing />
      </Suspense>
    </>
  );
}

function RequireSubscriptionFeature({ feature, requiredPlan = "Empresarial", children }) {
  const { hasFeature, loading, sub, setUpgradeFor } = useSubscription();

  useEffect(() => {
    if (!loading && !hasFeature(feature)) {
      setUpgradeFor({
        feature,
        requiredPlan,
        currentPlan: sub?.planLabel || "tu plan actual",
      });
    }
  }, [loading, hasFeature, feature, requiredPlan, setUpgradeFor, sub]);

  if (loading) return null;
  if (IS_LOCAL_EDITION) return children;
  if (!hasFeature(feature)) return <Navigate to="/suscripcion" replace />;
  return children;
}

function AppShell() {
  const { user } = useAuth();
  const { ambiente } = useAmbiente();
  const [catalogs, setCatalogs] = useState(null);
  const [progress, setProgress] = useState([]);
  const [alertasCount, setAlertasCount] = useState(0);
  const [theme, setTheme] = useState(() => localStorage.getItem("dteflow_theme") || "light");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const environmentKey = ambiente === "00" ? "00" : "01";
  const isTestsEnvironment = environmentKey === "00";

  const reloadProgress = () => dteApi.progress().then(d => setProgress(d.items || []));
  const reloadAlertas = () => dteApi.alertasList().then(d => setAlertasCount(d.summary?.critica + d.summary?.advertencia || 0)).catch(() => {});

  useEffect(() => {
    if (!user) return;
    dteApi.catalogs().then(setCatalogs);
    reloadProgress();
    reloadAlertas();
    const t = setInterval(reloadAlertas, 60_000);
    return () => clearInterval(t);
  }, [user, ambiente]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    root.dataset.ambiente = environmentKey;
    localStorage.setItem("dteflow_theme", theme);
    return () => {
      delete root.dataset.ambiente;
    };
  }, [theme, environmentKey]);

  return (
    <div className="app-shell flex flex-col min-h-screen bg-background" data-ambiente={environmentKey}>
      <QuotaBanner />
      <header className="environment-mobile-header lg:hidden sticky top-0 z-40 h-14 px-4 border-b border-border bg-white/95 backdrop-blur flex items-center justify-between">
        <button
          type="button"
          className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 text-slate-700"
          onClick={() => setMobileSidebarOpen(true)}
          aria-label="Abrir menú"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <img src="/logo-mark-192.png" alt="DTE Flow" className="h-7 w-7 object-contain" />
        </div>
        <button
          type="button"
          className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 text-slate-700"
          onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
          aria-label="Cambiar tema"
        >
          <span className="text-xs font-semibold">T</span>
        </button>
      </header>
      <div className="flex flex-1">
        <Sidebar
          className="hidden lg:flex"
          progress={progress}
          alertasCount={alertasCount}
          theme={theme}
          onToggleTheme={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
        />
        {mobileSidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <button
              type="button"
              className="absolute inset-0 bg-slate-900/40"
              aria-label="Cerrar menú"
              onClick={() => setMobileSidebarOpen(false)}
            />
            <div className="absolute inset-y-0 left-0">
              <Sidebar
                className="shadow-xl"
                progress={progress}
                alertasCount={alertasCount}
                theme={theme}
                onToggleTheme={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
                onNavigate={() => setMobileSidebarOpen(false)}
              />
            </div>
            <button
              type="button"
              className="absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-md bg-white border border-slate-200 text-slate-700"
              aria-label="Cerrar menú"
              onClick={() => setMobileSidebarOpen(false)}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <main className="environment-main flex-1 px-3 sm:px-5 lg:px-10 py-4 sm:py-6 lg:py-8 max-w-[1500px] w-full mx-auto min-w-0" data-testid="main-content">
          <div className="environment-context-bar" data-testid="environment-context-bar">
            <div className="environment-context-mark" aria-hidden="true">
              {isTestsEnvironment ? "00" : "01"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="environment-context-kicker">{isTestsEnvironment ? "Ambiente de pruebas" : "Ambiente de producción"}</div>
              <div className="environment-context-title">{isTestsEnvironment ? "Validación sin validez fiscal" : "Operación fiscal real"}</div>
            </div>
            <div className="environment-context-detail">
              {isTestsEnvironment
                ? "Tus pruebas MH están separadas de la operación real."
                : "Tus documentos se emiten con efectos fiscales."}
            </div>
          </div>
          <Suspense fallback={<div className="min-h-[40vh]" />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard progress={progress} />} />
            {/* Compatibilidad con enlaces antiguos: el selector vive ahora en la navegacion. */}
            <Route path="/ambiente" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/auto-pruebas"
              element={(
                <RequirePruebasEnvironment>
                  <AutoPruebas />
                </RequirePruebasEnvironment>
              )}
            />
            <Route path="/configuracion" element={<Configuracion catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/configuracion/guia" element={<GuiaConfiguracionFiscal />} />
            <Route path="/factura" element={<Factura catalogs={catalogs} reloadProgress={reloadProgress} />} />

            <Route path="/credito-fiscal" element={<DteForm tipoDte="03" catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/remision" element={<DteForm tipoDte="04" catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/nota-credito" element={<DteForm tipoDte="05" catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/nota-debito" element={<DteForm tipoDte="06" catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/liquidacion" element={<DteForm tipoDte="08" catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/doc-contable" element={<DteForm tipoDte="09" catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/exportacion" element={<DteForm tipoDte="11" catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/sujeto-excluido" element={<DteForm tipoDte="14" catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/donacion" element={<DteForm tipoDte="15" catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/retencion" element={<DteForm tipoDte="07" catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/invalidacion" element={<Invalidacion catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/contingencia" element={<Contingencia reloadProgress={reloadProgress} />} />
            <Route path="/evento-retorno" element={<EventoRetorno reloadProgress={reloadProgress} />} />
            <Route path="/retorno" element={<Navigate to="/evento-retorno" replace />} />

            {/* Nuevos módulos / renombrados */}
            <Route path="/documentos" element={<Historial catalogs={catalogs} reloadProgress={reloadProgress} />} />
            <Route path="/historial" element={<Navigate to="/documentos" replace />} />
            <Route path="/clientes" element={<Clientes />} />
            <Route path="/inventario" element={<Inventario />} />
            <Route
              path="/compras"
              element={(
                <RequireSubscriptionFeature feature="purchase_book_manual" requiredPlan="Pro">
                  <Recibidos />
                </RequireSubscriptionFeature>
              )}
            />
            <Route path="/recibidos" element={<Navigate to="/compras" replace />} />
            <Route
              path="/libros"
              element={(
                <RequireSubscriptionFeature feature="accounting_basic" requiredPlan="Pro">
                  <Contabilidad />
                </RequireSubscriptionFeature>
              )}
            />
            <Route path="/contabilidad" element={<Navigate to="/libros" replace />} />
            <Route
              path="/iva"
              element={(
                <RequireSubscriptionFeature feature="iva_auto" requiredPlan="Pro">
                  <Declaraciones />
                </RequireSubscriptionFeature>
              )}
            />
            <Route path="/declaraciones" element={<Navigate to="/iva" replace />} />
            <Route path="/alertas" element={<Alertas />} />
            <Route path="/importaciones" element={<Importaciones />} />
            <Route path="/reportes" element={<Reportes />} />
            {!IS_LOCAL_EDITION && (
              <Route
                path="/equipo"
                element={(
                  <RequireSubscriptionFeature feature="roles" requiredPlan="Empresarial">
                    <Equipo />
                  </RequireSubscriptionFeature>
                )}
              />
            )}
            {!IS_LOCAL_EDITION && (
              <Route
                path="/miembros"
                element={(
                  <RequireSubscriptionFeature feature="roles" requiredPlan="Empresarial">
                    <Miembros />
                  </RequireSubscriptionFeature>
                )}
              />
            )}
            {!IS_LOCAL_EDITION && <Route path="/suscripcion" element={<Suscripcion />} />}
            {!IS_LOCAL_EDITION && <Route path="/admin" element={<Admin />} />}
            {IS_LOCAL_EDITION && <Route path="/login" element={<Navigate to="/dashboard" replace />} />}
            {IS_LOCAL_EDITION && <Route path="/register" element={<Navigate to="/dashboard" replace />} />}
            {IS_LOCAL_EDITION && <Route path="/equipo" element={<Navigate to="/dashboard" replace />} />}
            {IS_LOCAL_EDITION && <Route path="/miembros" element={<Navigate to="/dashboard" replace />} />}
            {IS_LOCAL_EDITION && <Route path="/suscripcion" element={<Navigate to="/dashboard" replace />} />}
            {IS_LOCAL_EDITION && <Route path="/admin" element={<Navigate to="/dashboard" replace />} />}
          </Routes>
          </Suspense>
        </main>
        <AssistantWidget
          product={IS_LOCAL_EDITION ? "desktop" : "web_fiscal"}
          environment={IS_LOCAL_EDITION ? "any" : environmentKey}
        />
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="bottom-right" richColors offset={16} />
      <BrowserRouter>
        <MarketingAnalyticsBootstrap />
        <MarketingConsentBanner />
        <AuthProvider>
          <AmbienteProvider>
            <Routes>
              <Route path="/" element={<PublicLanding />} />
              <Route path="/login" element={IS_LOCAL_EDITION ? <Navigate to="/dashboard" replace /> : <Suspense fallback={null}><Login /></Suspense>} />
              <Route path="/register" element={IS_LOCAL_EDITION ? <Navigate to="/dashboard" replace /> : <Suspense fallback={null}><Register /></Suspense>} />
              <Route path="/onboarding" element={IS_LOCAL_EDITION ? <Navigate to="/dashboard" replace /> : <ProtectedRoute><Suspense fallback={null}><Onboarding /></Suspense></ProtectedRoute>} />
              <Route path="/forgot-password" element={IS_LOCAL_EDITION ? <Navigate to="/dashboard" replace /> : <Suspense fallback={null}><ForgotPassword /></Suspense>} />
              <Route path="/reset-password" element={IS_LOCAL_EDITION ? <Navigate to="/dashboard" replace /> : <Suspense fallback={null}><ResetPassword /></Suspense>} />
              <Route path="/invite/:token" element={IS_LOCAL_EDITION ? <Navigate to="/dashboard" replace /> : <Suspense fallback={null}><AcceptInvite /></Suspense>} />
              <Route path="/payment-result" element={IS_LOCAL_EDITION ? <Navigate to="/dashboard" replace /> : <Suspense fallback={null}><PaymentResult /></Suspense>} />
              <Route path="/privacy" element={<Suspense fallback={null}><PrivacyPolicy /></Suspense>} />
              <Route path="/terms" element={<Suspense fallback={null}><TermsOfService /></Suspense>} />
              <Route path="/*" element={
                <ProtectedRoute>
                  <SubscriptionProvider>
                    <IntegrationsHealthProvider>
                      <AppShell />
                      <UpgradeModal />
                    </IntegrationsHealthProvider>
                  </SubscriptionProvider>
                </ProtectedRoute>
              } />
            </Routes>
          </AmbienteProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
